import { Component, Host, h, Prop, State, Element } from "@stencil/core";
import { getStore, ILiftStatusStore } from "../../store";
import { ILiftStatusWebComponentError } from "../../utils/types";
import { watchStoreLocale } from "../../utils/locale-state";
import { getStoreUnavailableError } from "../../utils/store-error";

@Component({
  tag: "ls-status",
  styleUrl: "ls-status.css",
  shadow: true,
})
export class LsStatus {
  private static readonly DEFAULT_FONT_SIZE_PX = 16;
  @Element() hostEl: HTMLElement;
  /**
   * Specifies the IDs of the sensors that are to be displayed.
   */
  @Prop() readonly sensorIds = "500,501,502,503,514,515,590";

  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  /**
   * Selects which label set to use for status items.
   */
  @Prop() readonly labelType: "default" | "short" = "default";

  /**
   * The sensor IDs input property, converted to an array of numbers.
   */
  @State() sensorIdsArray = this.sensorIds
    .split(",")
    .filter((id) => id.trim() !== "")
    .map((id) => parseInt(id.trim(), 10));

  /**
   * Centralized error state for tracking component errors
   */
  @State() errorState: ILiftStatusWebComponentError = {
    hasError: false,
    messageKey: undefined,
    type: "",
  };

  @State() useTwoColumns = false;

  private store: ILiftStatusStore;
  private storeUnsubscribe: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;

  async componentWillLoad() {
    this.store = await getStore(this.idKey);
    watchStoreLocale(this, this.store);
    this.filterBackdoorSensor(this.store.state.hasBackDoor);
    this.updateErrorState();

    if (this.store !== null) {
      this.storeUnsubscribe = this.store.onChange("error", () => {
        this.updateErrorState();
      });
    }
  }

  componentDidLoad() {
    // Initialize and keep the column layout in sync with host size changes.
    this.updateColumnLayout();

    if (typeof ResizeObserver === "undefined") return;

    this.resizeObserver = new ResizeObserver(() => {
      this.updateColumnLayout();
    });
    this.resizeObserver.observe(this.hostEl);
  }

  disconnectedCallback() {
    if (this.storeUnsubscribe !== null) {
      this.storeUnsubscribe();
      this.storeUnsubscribe = null;
    }

    if (this.resizeObserver !== null) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  /**
   * Updates the error state based on current component conditions
   */
  private updateErrorState() {
    if (this.store === null) {
      this.errorState = getStoreUnavailableError(this.store?.state.locale);
      return;
    }

    if (this.store.state.error.hasError) {
      this.errorState = {
        hasError: true,
        messageKey: this.store.state.error.messageKey,
        type: this.store.state.error.type,
      };
      return;
    }

    this.errorState = {
      hasError: false,
      messageKey: undefined,
      type: "",
    };
  }

  private filterBackdoorSensor = (hasBackDoor: boolean) => {
    if (hasBackDoor) return;

    this.sensorIdsArray = this.sensorIdsArray.filter((sensor) => {
      return sensor !== 503;
    });
  };

  private parseLengthToPx(value: string): number | null {
    // Support only px/em/rem values for the grid breakpoint.
    if (value.trim() === "") return null;

    const numeric = parseFloat(value);
    if (Number.isNaN(numeric) || numeric < 0) return null;

    if (value.endsWith("px")) {
      return numeric;
    }

    if (value.endsWith("rem")) {
      const rootFontSizeValue = getComputedStyle(
        document.documentElement,
      ).fontSize;
      const rootFontSize = parseFloat(rootFontSizeValue);
      return (
        numeric *
        (Number.isNaN(rootFontSize)
          ? LsStatus.DEFAULT_FONT_SIZE_PX
          : rootFontSize)
      );
    }

    if (value.endsWith("em")) {
      const hostFontSizeValue = getComputedStyle(this.hostEl).fontSize;
      const hostFontSize = parseFloat(hostFontSizeValue);
      return (
        numeric *
        (Number.isNaN(hostFontSize)
          ? LsStatus.DEFAULT_FONT_SIZE_PX
          : hostFontSize)
      );
    }

    return null;
  }

  private getErrorMessageForDisplay(): string {
    const errorKey = this.errorState.messageKey ?? "CONSOLE_INFO";
    return this.store.t(`errors:${errorKey}`);
  }

  private updateColumnLayout() {
    // Toggle two-column layout when the host width meets the breakpoint.
    if (this.hostEl === null) return;

    const computedStyles = getComputedStyle(this.hostEl);
    const breakpointValue = computedStyles
      .getPropertyValue("--ls-status-grid-breakpoint")
      .trim();
    const breakpointPx = this.parseLengthToPx(breakpointValue);

    if (breakpointPx === null) {
      this.useTwoColumns = false;
      return;
    }

    const width = this.hostEl.getBoundingClientRect().width;
    const shouldUseTwoColumns = width >= breakpointPx;

    if (this.useTwoColumns !== shouldUseTwoColumns) {
      this.useTwoColumns = shouldUseTwoColumns;
    }
  }

  render() {
    const contentClass = {
      content: true,
      "two-columns": this.useTwoColumns,
      blur: this.errorState.hasError,
    };

    return (
      <Host>
        <slot></slot>
        <div class={contentClass}>
          {this.sensorIdsArray.map((sensor) => (
            <ls-status-item
              sensor-id={sensor}
              id-key={this.idKey}
              label-type={this.labelType}
            ></ls-status-item>
          ))}
        </div>
        {this.errorState.hasError && (
          <div class="error-message">{this.getErrorMessageForDisplay()}</div>
        )}
      </Host>
    );
  }
}
